import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  Pin,
  InfoWindow,
} from '@vis.gl/react-google-maps';
import { useEffect, useRef, useState } from 'react';

interface Marker {
  id: string;
  name: string;
  position: {
    lat: number;
    lng: number;
  };
  description?: string;
  endereco?: string;
  telefone?: string;
}

interface SimpleMapProps {
  markers: Marker[];
  mapId?: string;
  selectedMarkerId: string | null;
  centerOverride?: { lat: number; lng: number } | null;
}

const SimpleMap: React.FC<SimpleMapProps> = ({
  markers,
  mapId,
  selectedMarkerId,
  centerOverride,
}) => {
  const [openMarkerId, setOpenMarkerId] = useState<string | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const apiKey = process.env.REACT_APP_GOOGLE_API_TOKEN;
  const defaultMapId = process.env.REACT_APP_GOOGLE_MAP_ID;

  useEffect(() => {
    if (selectedMarkerId) {
      setOpenMarkerId(selectedMarkerId);
    }
  }, [selectedMarkerId]);

  const center =
    centerOverride ||
    (markers.length > 0
      ? markers[0].position
      : { lat: -15.7801, lng: -47.9292 });

  if (!apiKey) {
    console.error('API Key do Google Maps não definida!');
    return (
      <div className="p-4 bg-red-100 text-red-700 rounded">
        Erro: API Key do Google Maps não definida.
      </div>
    );
  }

  return (
    <APIProvider apiKey={apiKey}>
      <div className="w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] rounded-lg overflow-hidden">
        {markers.length === 0 ? (
          <div className="flex items-center justify-center h-1/2 text-gray-500">
            Nenhum marcador disponível.
          </div>
        ) : (
          <GoogleMap
            center={center}
            zoom={13}
            minZoom={5}
            mapId={mapId || defaultMapId}
            className="w-full h-full"
          >
            {markers.map(marker => (
              <div key={marker.id}>
                <AdvancedMarker
                  position={marker.position}
                  onClick={() =>
                    setOpenMarkerId(prev =>
                      prev === marker.id ? null : marker.id,
                    )
                  }
                >
                  <Pin background="#3B82F6" glyphColor="white" />
                </AdvancedMarker>

                {(openMarkerId === marker.id ||
                  selectedMarkerId === marker.id) && (
                  <InfoWindow
                    position={marker.position}
                    onCloseClick={() => setOpenMarkerId(null)}
                  >
                    <div className="p-2 text-sm w-7/l rounded shadow-lg">
                      <h4 className="font-bold text-blue-700">{marker.name}</h4>
                      {marker.description && (
                        <p className="text-gray-700">{marker.description}</p>
                      )}
                      {marker.endereco && (
                        <p className="text-gray-600">
                          <strong>Endereço:</strong> {marker.endereco}
                        </p>
                      )}
                      {marker.telefone && (
                        <p className="text-gray-600">
                          <strong>Telefone:</strong> {marker.telefone}
                        </p>
                      )}
                    </div>
                  </InfoWindow>
                )}
              </div>
            ))}
          </GoogleMap>
        )}
      </div>
    </APIProvider>
  );
};

export default SimpleMap;
